import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import { DemoPersonaRole } from '../../../services/demoService';
import {
  PERSONA_DOORS,
  PERSONA_DOORS_HEADING,
  PERSONA_DOORS_SECTION_ID,
} from '../demoContent';
import IconTile from './IconTile';
import SectionHeading from './SectionHeading';

interface PersonaDoorsProps {
  onOpen: (role: DemoPersonaRole) => void;
  loading: boolean;
  pending: DemoPersonaRole | null;
}

/** The four ways in. Each card is a real button (CardActionArea), so keyboard entry works. */
export default function PersonaDoors({ onOpen, loading, pending }: PersonaDoorsProps) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      id={PERSONA_DOORS_SECTION_ID}
      sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 11 }, scrollMarginTop: '120px' }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          overline={PERSONA_DOORS_HEADING.overline}
          heading={PERSONA_DOORS_HEADING.heading}
          sub={PERSONA_DOORS_HEADING.sub}
        />

        <Grid container spacing={3}>
          {PERSONA_DOORS.map((door, index) => (
            <Grid item xs={12} sm={6} md={3} key={door.role}>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: index * 0.07, ease: 'easeOut' }}
                style={{ height: '100%' }}
              >
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderRadius: 2.5,
                    borderColor: 'divider',
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 28px rgba(26, 36, 112, 0.14)',
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                    },
                    '&:hover .door-arrow': { transform: 'translateX(4px)' },
                  }}
                >
                  <CardActionArea
                    data-demo-id={`demo-door-${door.role}`}
                    onClick={() => onOpen(door.role)}
                    disabled={loading}
                    sx={{
                      height: '100%',
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {pending === door.role ? (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      <IconTile Icon={door.Icon} accent="navy" />
                    )}

                    <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                      {door.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, flexGrow: 1 }}>
                      {door.blurb}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ mt: 2.5, color: 'primary.main' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Enter
                      </Typography>
                      <ArrowForwardIcon
                        className="door-arrow"
                        sx={{ fontSize: 16, transition: 'transform 0.3s ease' }}
                      />
                    </Stack>
                  </CardActionArea>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
